import { Button, Icon, Label, Typography } from '@packages/ui/shared';
import { useCallback } from 'react';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { FailCodeSelection, FailCodeVehicleParams } from './fail-codes-modal.types';

import { useLookupBySection } from './hooks/use-lookup-by-section';

/**
 * Props for the {@link LookupBySection} component.
 */
export type LookupBySectionProps = {
    /** Callback invoked when the user navigates back to the search method selector. */
    onBack: () => void;

    /** Callback invoked when the modal should close. */
    onClose: () => void;

    /**
     * Callback invoked when the user confirms a fail code selection.
     * Receives the completed {@link FailCodeSelection} to persist on the case.
     */
    onConfirmFailCode: (selection: FailCodeSelection) => void;

    /** Vehicle params used to scope fail code lookups, already mapped from VIN data by the parent. */
    vehicleParams: FailCodeVehicleParams;
};

/**
 * Lookup-by-section fail code search method. Presents a 4-level cascading Typeahead flow:
 * section -> subsection -> figure/repair-type -> fail code. The user must explicitly select
 * a value at each level before the next level becomes available. Delegates all state management
 * and API interactions to {@link useLookupBySection}.
 *
 * @param props - {@link LookupBySectionProps}
 * @returns the lookup by section JSX
 */
const useLookupBySectionStyles = createUseStyles({
    buttonRow: {
        display: 'flex',
        gap: 15,
    },
});

/**
 * LookupBySection component renders the fail code search flow by section.
 * Guides the user through a cascading selection of section, subsection, figure, and fail code.
 *
 * @param props - LookupBySectionProps
 * @returns JSX.Element
 */
export function LookupBySection({ onBack, onClose, onConfirmFailCode, vehicleParams }: LookupBySectionProps) {
    // Hooks
    const { t } = useTranslation();
    const classes = useLookupBySectionStyles();

    // Self-contained read-only hook exception: this hook performs only read-only fail code
    // lookups. The parent does not participate in any intermediate cascade state. The only
    // output is the final FailCodeSelection delivered via onConfirmFailCode.
    const {
        confirmSelection,
        failCodeOptionData,
        figureOptionData,
        handleFailCodeChange,
        handleFigureChange,
        handleSectionChange,
        handleSubsectionChange,
        isConfirmDisabled,
        isConfirming,
        sectionOptionData,
        selectedFailCode,
        selectedFigure,
        selectedSection,
        selectedSubsection,
        subsectionOptionData,
    } = useLookupBySection(vehicleParams);

    // Forwarded callback aliases
    const handleBack = onBack;
    const handleClose = onClose;

    // Handlers

    /**
     * Handle the confirm button press. Builds the final fail code selection from the hook's
     * state and passes it to the parent, then closes the modal.
     */
    const handleConfirmButtonPress = useCallback(() => {
        const selection = confirmSelection();
        if (selection) {
            onConfirmFailCode(selection);
            handleClose();
        }
    }, [confirmSelection, handleClose, onConfirmFailCode]);

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>{t('qmr:sections:failCode.lookupBySection')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form>
                    <Row>
                        <Col className="mb-4" xs={12}>
                            <Label>{t('qmr:sections:failCode.section')}</Label>
                            <Typeahead
                                id="fail-code-search-modal__section"
                                onChange={handleSectionChange}
                                options={sectionOptionData}
                                placeholder={t('qmr:sections:failCode.chooseSection')}
                                selected={selectedSection}
                            />
                        </Col>

                        <Col className="mb-4" xs={12}>
                            <Label>{t('qmr:sections:failCode.subsection')}</Label>
                            <Typeahead
                                disabled={selectedSection.length === 0}
                                id="fail-code-search-modal__subsection"
                                onChange={handleSubsectionChange}
                                options={subsectionOptionData}
                                placeholder={t('qmr:sections:failCode.chooseSubsection')}
                                selected={selectedSubsection}
                            />
                        </Col>

                        <Col className="mb-4" xs={12}>
                            <Label>{t('qmr:sections:failCode.repairType')}</Label>
                            <Typeahead
                                disabled={selectedSubsection.length === 0}
                                id="fail-code-search-modal__repair-type"
                                onChange={handleFigureChange}
                                options={figureOptionData}
                                placeholder={t('qmr:sections:failCode.chooseFigure')}
                                selected={selectedFigure}
                            />
                        </Col>

                        <Col className="mb-4" xs={12}>
                            <Label>{t('qmr:sections:failCode.failedPart')}</Label>
                            <Typeahead
                                disabled={selectedFigure.length === 0}
                                id="fail-code-search-modal__failed-part"
                                onChange={handleFailCodeChange}
                                options={failCodeOptionData}
                                placeholder={t('qmr:sections:failCode.chooseFailCode')}
                                selected={selectedFailCode}
                            />
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button
                    iconLeft={<Icon color="blueOne" name="chevron-left" />}
                    onPress={handleBack}
                    variant="ghost-blue"
                >
                    {t('modals:failCodeSearch.actions.back', 'Back')}
                </Button>

                <div className={classes.buttonRow}>
                    <Button onPress={onClose} variant="link-blue">
                        <Typography color="blueOne">{t('qmr:sections:failCode.cancel')}</Typography>
                    </Button>
                    <Button
                        disabled={isConfirmDisabled}
                        isLoading={isConfirming}
                        onPress={handleConfirmButtonPress}
                        variant="primary"
                    >
                        {t('qmr:sections:failCode.addFailCode')}
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}
